import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import {
  getUserAppAllPage,
  deleteUserApp,
} from "./ServiceUserApp";

import ConfirmationModal from "../../components/home/ConfirmationModal";

export default function ListUserApp() {

  const { t } = useTranslation();

  // ============================================================
  // PAGINATION
  // ============================================================

  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [nombrEnreg, setNombreEnrg] = useState(5);

  // ============================================================
  // RECHERCHE
  // ============================================================

  const [search, setSearch] = useState("");

  // ============================================================
  // TRI
  // ============================================================

  const [order, setOrder] = useState("ASC");

  // ============================================================
  // CHAMPS DE TRI
  // ============================================================

  const [selectedValues, setSelectedValues] = useState([]);

  // ============================================================
  // DONNEES
  // ============================================================

  const [userAppData, setUserAppData] = useState([]);

  const [loading, setLoading] = useState(false);

  // ============================================================
  // SUPPRESSION
  // ============================================================

  const [userAppId, setUserAppId] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState(false);

  // ============================================================
  // PAGINATION
  // ============================================================

  const firstPage = () => {
    setCurrentPage(0);
  };

  const lastPage = () => {
    if (totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  };

  const prePage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const nextPage = () => {
    setCurrentPage((prev) =>
      Math.min(prev + 1, Math.max(totalPages - 1, 0))
    );
  };

  // ============================================================
  // TRI ASC
  // ============================================================

  const sortTableAsc = () => {
    setOrder("ASC");
  };

  // ============================================================
  // TRI DESC
  // ============================================================

  const sortTableDesc = () => {
    setOrder("DESC");
  };

  // ============================================================
  // CHECKBOX CHAMPS DE TRI
  // ============================================================

  const handleCheckBoxChange = (e) => {

    const value = e.target.value;
    const checked = e.target.checked;

    setSelectedValues((prev) => {

      if (checked) {

        if (prev.includes(value)) {
          return prev;
        }

        return [...prev, value];
      }

      return prev.filter((val) => val !== value);
    });

    setCurrentPage(0);
  };

  // ============================================================
  // CHARGEMENT DES UTILISATEURS
  // ============================================================

  const loadUserApp = useCallback(async () => {

    setLoading(true);

    try {

      const selectedFields = selectedValues.join(",");

      const result = await getUserAppAllPage(
        search,
        currentPage,
        nombrEnreg,
        selectedFields,
        order
      );

      const data = result?.data;

      setUserAppData(data?.content ?? []);
      setTotalPages(data?.totalPages ?? 0);

    } catch (error) {

      console.error(
        "Erreur lors du chargement des utilisateurs :",
        error
      );

      setUserAppData([]);
      setTotalPages(0);

      toast.error(
        t("utilisateur.loadingError", {
          defaultValue: "Impossible de charger les utilisateurs.",
        }),
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        }
      );

    } finally {

      setLoading(false);
    }

  }, [
    search,
    currentPage,
    nombrEnreg,
    selectedValues,
    order,
    t,
  ]);

  // ============================================================
  // EFFECT CHARGEMENT
  // ============================================================

  useEffect(() => {

    loadUserApp();

  }, [loadUserApp]);

  // ============================================================
  // CORRECTION PAGE COURANTE
  // ============================================================

  useEffect(() => {

    if (totalPages > 0 && currentPage >= totalPages) {
      setCurrentPage(totalPages - 1);
    }

    if (totalPages === 0 && currentPage !== 0) {
      setCurrentPage(0);
    }

  }, [currentPage, totalPages]);

  // ============================================================
  // SELECTION UTILISATEUR A SUPPRIMER
  // ============================================================

  const selectId = (id) => {

    setUserAppId(id);
    setShowConfirmationModal(true);
  };

  // ============================================================
  // FERMER MODAL
  // ============================================================

  const handleCloseModal = () => {

    setShowConfirmationModal(false);
    setUserAppId(null);
  };

  // ============================================================
  // SUPPRIMER UTILISATEUR
  // ============================================================

  const deleteuserApp = async () => {

    if (!userAppId) {
      return;
    }

    try {

      await deleteUserApp(userAppId);

      setShowConfirmationModal(false);
      setUserAppId(null);

      toast.success(
        t("common.deleteSuccess"),
        {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Recharger la liste
      await loadUserApp();

    } catch (error) {

      console.error(
        "Erreur lors de la suppression :",
        error
      );

      if (
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {

        const errorArray = error.response.data.errors;

        const formattedErrors = errorArray.map(
          (errorMessage, index) => (
            <li
              className="list-group-item"
              key={index}
            >
              {errorMessage}
            </li>
          )
        );

        toast.error(
          <div style={{ width: "20rem" }}>

            <div className="card-header text-danger">
              <h6 className="card-title mb-0">
                {t("utilisateur.deleteError", {
                  defaultValue:
                    "Erreurs lors de la suppression",
                })}
              </h6>
            </div>

            <div className="card-body">

              <ul className="list-group list-group-flush">
                {formattedErrors}
              </ul>

            </div>

          </div>,
          {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 4000,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );

      } else {

        toast.error(
          t("utilisateur.deleteError", {
            defaultValue:
              "Impossible de supprimer l'utilisateur.",
          }),
          {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 3000,
          }
        );
      }

      setShowConfirmationModal(false);
    }
  };

  // ============================================================
  // TYPE UTILISATEUR
  // ============================================================

  const getUserType = (user) => {

    /*
     * IMPORTANT :
     * Cette fonction devra être adaptée aux valeurs
     * exactes de ton enum FalahOrProfType.
     */

    if (!user) {
      return {
        label: t("utilisateur.unknownType", {
          defaultValue: "Inconnu",
        }),
        className: "bg-secondary",
      };
    }

    const falahOrProf = user.falahOrProf?.toString()?.toUpperCase();

    const natureActivityType =
      user.natureActivityType?.toString()?.toUpperCase();

    // ----------------------------------------------------------
    // FELLAH
    // ----------------------------------------------------------

    if (
      falahOrProf === "F" ||
      falahOrProf === "FELAH" ||
      falahOrProf === "FELLAH"
    ) {

      return {
        label: t("utilisateur.typeFelah", {
          defaultValue: "Fellah",
        }),
        className: "bg-success",
      };
    }

    // ----------------------------------------------------------
    // PROFESSIONNEL
    // ----------------------------------------------------------

    if (
      falahOrProf === "P" ||
      falahOrProf === "PROF" ||
      falahOrProf === "PROFESSIONNEL"
    ) {

      return {
        label: t("utilisateur.typeProf", {
          defaultValue: "Professionnel",
        }),
        className: "bg-primary",
      };
    }

    // ----------------------------------------------------------
    // FOURNISSEUR
    // ----------------------------------------------------------

    if (
      falahOrProf === "FO" ||
      falahOrProf === "FOURNISSEUR" ||
      natureActivityType === "FO" ||
      natureActivityType === "FOURNISSEUR"
    ) {

      return {
        label: t("utilisateur.typeFournisseur", {
          defaultValue: "Fournisseur",
        }),
        className: "bg-warning text-dark",
      };
    }

    // ----------------------------------------------------------
    // TYPE INCONNU
    // ----------------------------------------------------------

    return {
      label:
        user.falahOrProf ||
        user.natureActivityType ||
        t("utilisateur.unknownType", {
          defaultValue: "Inconnu",
        }),

      className: "bg-secondary",
    };
  };

  // ============================================================
  // AFFICHAGE PAGINATION
  // ============================================================

  const renderPagination = () => {

    if (totalPages <= 1) {
      return null;
    }

    const pages = [];

    const range = 1;

    const startRange = Math.max(
      currentPage - range,
      0
    );

    const endRange = Math.min(
      currentPage + range,
      totalPages - 1
    );

    // Première page
    if (startRange > 0) {

      pages.push(
        <li
          key="first-page"
          className="page-item"
        >
          <button
            className="page-link"
            onClick={() => setCurrentPage(0)}
          >
            1
          </button>
        </li>
      );

      if (startRange > 1) {

        pages.push(
          <li
            key="start-dots"
            className="page-item disabled"
          >
            <span className="page-link">
              ...
            </span>
          </li>
        );
      }
    }

    // Pages centrales
    for (
      let index = startRange;
      index <= endRange;
      index++
    ) {

      pages.push(
        <li
          key={index}
          className={`page-item ${
            currentPage === index
              ? "active"
              : ""
          }`}
        >
          <button
            className="page-link"
            onClick={() =>
              setCurrentPage(index)
            }
          >
            {index + 1}
          </button>
        </li>
      );
    }

    // Dernière page
    if (endRange < totalPages - 1) {

      if (endRange < totalPages - 2) {

        pages.push(
          <li
            key="end-dots"
            className="page-item disabled"
          >
            <span className="page-link">
              ...
            </span>
          </li>
        );
      }

      pages.push(
        <li
          key="last-page"
          className="page-item"
        >
          <button
            className="page-link"
            onClick={() =>
              setCurrentPage(totalPages - 1)
            }
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return pages;
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (

    <div
      className="container-fluid px-4"
      style={{ marginTop: "5rem" }}
    >

      {/* ======================================================
          BREADCRUMB
      ====================================================== */}

      <nav className="mb-3">

        <ol className="breadcrumb bg-white px-3 py-2 shadow-sm rounded-3">

          <li className="breadcrumb-item">

            <Link
              to="/"
              className="text-decoration-none"
            >
              <i className="fa-solid fa-house me-1"></i>

              {t("menu.home")}
            </Link>

          </li>

          <li className="breadcrumb-item active">

            {t("utilisateur.list", {
              defaultValue: t("utilisateur.list"),
            })}

          </li>

        </ol>

      </nav>

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="card shadow-sm border-0 mb-3">

        <div className="card-body d-flex justify-content-between align-items-center">

          <div>

            <h4 className="mb-1 fw-bold text-primary">

              <i className="fa-solid fa-users me-2"></i>

              {t("utilisateur.list" )}

            </h4>

            <small className="text-muted">

              {t("utilisateur.listDescription", {
                defaultValue:
                  "Gestion des utilisateurs de la plateforme",
              })}

            </small>

          </div>

        

        </div>

      </div>

      {/* ======================================================
          FILTRES
      ====================================================== */}

      <div className="card shadow-sm border-0 mb-3">

        <div className="card-body">

          <div className="row g-3 align-items-center">

            {/* Recherche */}

            <div className="col-lg-6">

              <div className="input-group">

                <span className="input-group-text bg-white">

                  <i className="fa-solid fa-magnifying-glass"></i>

                </span>

                <input
                  type="search"
                  className="form-control"
                  value={search}
                  placeholder={t("button.search")}
                  onChange={(e) => {

                    setSearch(e.target.value);
                    setCurrentPage(0);

                  }}
                />

                {search && (

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {

                      setSearch("");
                      setCurrentPage(0);

                    }}
                    title={t("common.clear", {
                      defaultValue: "Effacer",
                    })}
                  >
                    <i className="fa-solid fa-xmark"></i>
                  </button>

                )}

              </div>

            </div>

            {/* Nombre de lignes */}

            <div className="col-lg-3">

              <select
                className="form-select"
                value={nombrEnreg}
                onChange={(e) => {

                  setNombreEnrg(
                    Number(e.target.value)
                  );

                  setCurrentPage(0);

                }}
              >

                <option value={5}>
                  5 {t("common.rows")}
                </option>

                <option value={10}>
                  10 {t("common.rows")}
                </option>

                <option value={20}>
                  20 {t("common.rows")}
                </option>

                <option value={50}>
                  50 {t("common.rows")}
                </option>

              </select>

            </div>

            {/* Tri */}

            <div className="col-lg-3">

              <div className="d-flex justify-content-lg-end">

                <div className="btn-group">

                  <button
                    type="button"
                    className={`btn btn-outline-primary ${
                      order === "ASC"
                        ? "active"
                        : ""
                    }`}
                    onClick={sortTableAsc}
                    disabled={
                      selectedValues.length === 0
                    }
                    title={t("common.sortAscending", {
                      defaultValue:
                        "Tri croissant",
                    })}
                  >

                    <i className="fa-solid fa-arrow-up-a-z"></i>

                  </button>

                  <button
                    type="button"
                    className={`btn btn-outline-primary ${
                      order === "DESC"
                        ? "active"
                        : ""
                    }`}
                    onClick={sortTableDesc}
                    disabled={
                      selectedValues.length === 0
                    }
                    title={t("common.sortDescending", {
                      defaultValue:
                        "Tri décroissant",
                    })}
                  >

                    <i className="fa-solid fa-arrow-down-z-a"></i>

                  </button>

                </div>

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* ======================================================
          TABLE
      ====================================================== */}

      <div className="card shadow-sm border-0">

        <div className="table-responsive">

          <table className="table table-hover align-middle mb-0">

            <thead className="table-dark">

              <tr>

                {/* Full Name */}

                <th className="px-3 py-3">

                  <div className="d-flex align-items-center gap-2">

                    <input
                      type="checkbox"
                      value="fullName"
                      checked={selectedValues.includes(
                        "fullName"
                      )}
                      onChange={
                        handleCheckBoxChange
                      }
                    />

                    <span>
                      {t("utilisateur.fullName", {
                        defaultValue:
                          "Nom complet",
                      })}
                    </span>

                  </div>

                </th>

                {/* Email */}

                <th className="px-3 py-3">

                  <div className="d-flex align-items-center gap-2">

                    <input
                      type="checkbox"
                      value="email"
                      checked={selectedValues.includes(
                        "email"
                      )}
                      onChange={
                        handleCheckBoxChange
                      }
                    />

                    <span>
                      {t("utilisateur.email", {
                        defaultValue:
                          "Email",
                      })}
                    </span>

                  </div>

                </th>

                {/* Téléphone */}

                <th className="px-3 py-3">

                  <div className="d-flex align-items-center gap-2">

                    <input
                      type="checkbox"
                      value="phoneNumber"
                      checked={selectedValues.includes(
                        "phoneNumber"
                      )}
                      onChange={
                        handleCheckBoxChange
                      }
                    />

                    <span>
                      {t("utilisateur.phoneNumber", {
                        defaultValue:
                          "Téléphone",
                      })}
                    </span>

                  </div>

                </th>

                {/* Type */}

                <th className="px-3 py-3">

                  <div className="d-flex align-items-center gap-2">

                    <input
                      type="checkbox"
                      value="falahOrProf"
                      checked={selectedValues.includes(
                        "falahOrProf"
                      )}
                      onChange={
                        handleCheckBoxChange
                      }
                    />

                    <span>
                      {t("utilisateur.type", {
                        defaultValue:
                          "Type",
                      })}
                    </span>

                  </div>

                </th>

                {/* Actions */}

                <th className="text-center px-3 py-3">

                  {t("common.actions")}

                </th>

              </tr>

            </thead>

            <tbody>

              {/* Chargement */}

              {loading && (

                <tr>

                  <td
                    colSpan="5"
                    className="text-center py-5"
                  >

                    <div
                      className="spinner-border text-primary"
                      role="status"
                    >

                      <span className="visually-hidden">
                        {t("common.loading")}
                      </span>

                    </div>

                    <div className="mt-2 text-muted">
                      {t("common.loading")}
                    </div>

                  </td>

                </tr>

              )}

              {/* Aucun utilisateur */}

              {!loading &&
                userAppData.length === 0 && (

                  <tr>

                    <td
                      colSpan="5"
                      className="text-center py-5 text-muted"
                    >

                      <i className="fa-solid fa-users-slash fa-2x mb-3"></i>

                      <div>

                        {t("utilisateur.noUsers", {
                          defaultValue:
                            "Aucun utilisateur trouvé.",
                        })}

                      </div>

                    </td>

                  </tr>

                )}

              {/* Liste utilisateurs */}

              {!loading &&
                userAppData.map((item) => {

                  const userType =
                    getUserType(item);

                  return (

                    <tr key={item.id}>

                      {/* Nom */}

                      <td className="px-3">

                        <div className="d-flex align-items-center">

                          <div
                            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2"
                            style={{
                              width: "38px",
                              height: "38px",
                              minWidth: "38px",
                            }}
                          >

                            <i className="fa-solid fa-user"></i>

                          </div>

                          <div>

                            <div className="fw-semibold">

                              {item.fullName ||
                                "-"}

                            </div>

                          </div>

                        </div>

                      </td>

                      {/* Email */}

                      <td className="px-3">

                        {item.email ? (

                          <a
                            href={`mailto:${item.email}`}
                            className="text-decoration-none"
                          >

                            <i className="fa-solid fa-envelope me-2 text-muted"></i>

                            {item.email}

                          </a>

                        ) : (
                          "-"
                        )}

                      </td>

                      {/* Téléphone */}

                      <td className="px-3">

                        {item.phoneNumber ? (

                          <a
                            href={`tel:${item.phoneNumber}`}
                            className="text-decoration-none"
                          >

                            <i className="fa-solid fa-phone me-2 text-muted"></i>

                            {item.phoneNumber}

                          </a>

                        ) : (
                          "-"
                        )}

                      </td>

                      {/* Type */}

                      <td className="px-3">

                        <span
                          className={`badge ${userType.className} px-3 py-2`}
                        >

                          {userType.label}

                        </span>

                      </td>

                      {/* Actions */}

                      <td className="text-center px-3">

                        <Link
                          to={`/updateuserApp/${item.id}`}
                          className="btn btn-sm btn-outline-success rounded-circle me-2"
                          title={t("common.edit", {
                            defaultValue:
                              "Modifier",
                          })}
                        >

                          <i className="fa-solid fa-pen"></i>

                        </Link>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger rounded-circle"
                          onClick={() =>
                            selectId(item.id)
                          }
                          title={t("common.delete", {
                            defaultValue:
                              "Supprimer",
                          })}
                        >

                          <i className="fa-solid fa-trash"></i>

                        </button>

                      </td>

                    </tr>

                  );
                })}

            </tbody>

          </table>

        </div>

        {/* ====================================================
            FOOTER PAGINATION
        ==================================================== */}

        <div className="card-footer bg-white border-0">

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">

            <small className="text-muted">

              {t("utilisateur.page", {
                defaultValue: "Page",
              })}{" "}

              {totalPages > 0
                ? currentPage + 1
                : 0}

              {" / "}

              {totalPages}

            </small>

            <nav>

              <ul className="pagination justify-content-center mb-0">

                <li
                  className={`page-item ${
                    currentPage === 0 ||
                    totalPages === 0
                      ? "disabled"
                      : ""
                  }`}
                >

                  <button
                    className="page-link"
                    onClick={firstPage}
                  >
                    «
                  </button>

                </li>

                <li
                  className={`page-item ${
                    currentPage === 0 ||
                    totalPages === 0
                      ? "disabled"
                      : ""
                  }`}
                >

                  <button
                    className="page-link"
                    onClick={prePage}
                  >
                    ‹
                  </button>

                </li>

                {renderPagination()}

                <li
                  className={`page-item ${
                    currentPage >=
                      totalPages - 1 ||
                    totalPages === 0
                      ? "disabled"
                      : ""
                  }`}
                >

                  <button
                    className="page-link"
                    onClick={nextPage}
                  >
                    ›
                  </button>

                </li>

                <li
                  className={`page-item ${
                    currentPage >=
                      totalPages - 1 ||
                    totalPages === 0
                      ? "disabled"
                      : ""
                  }`}
                >

                  <button
                    className="page-link"
                    onClick={lastPage}
                  >
                    »
                  </button>

                </li>

              </ul>

            </nav>

          </div>

        </div>

      </div>

      {/* ======================================================
          MODAL CONFIRMATION
      ====================================================== */}

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onConfirm={deleteuserApp}
        onCancel={handleCloseModal}
      />

    </div>
  );
}